import { Box, Container, Typography, Stack } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { useNavigate } from 'react-router-dom'
import { Button, Icon } from '@/components/common'
import { heroContent } from '@/data/content'
import { SECTION_IDS } from '@/utils/constants'

/**
 * Hero Section Component
 * Landing section with headline, value points, and CTAs
 */
export const HeroSection = () => {
  const navigate = useNavigate()

  const handlePrimaryCTA = () => {
    navigate('/contact')
  }

  const handleSecondaryCTA = () => {
    navigate('/pricing')
  }

  return (
    <Box
      component="section"
      id={SECTION_IDS.HOME}
      sx={{
        minHeight: { xs: '500px', md: '600px' },
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        background: `linear-gradient(135deg, rgba(30, 64, 175, 0.92) 0%, rgba(15, 27, 46, 0.85) 100%), url(/assets/images/hero-image.png)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        color: 'white',
        py: { xs: 8, md: 12 },
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            textAlign: 'center',
            maxWidth: '900px',
            mx: 'auto',
          }}
        >
          {/* Headline */}
          <Typography
            variant="h1"
            component="h1"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
              mb: 3,
              lineHeight: 1.2,
            }}
          >
            {heroContent.headline}
          </Typography>

          {/* Subheadline */}
          <Typography
            variant="h6"
            component="p"
            sx={{
              fontSize: { xs: '1rem', md: '1.25rem' },
              mb: 4,
              opacity: 0.95,
              lineHeight: 1.6,
              fontWeight: 400,
            }}
          >
            {heroContent.subheadline}
          </Typography>

          {/* CTA Buttons */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{
              justifyContent: 'center',
              mb: 6,
            }}
          >
            <Button
              variant="primary"
              size="large"
              onClick={handlePrimaryCTA}
              sx={{
                bgcolor: 'white',
                color: '#1e40af',
                '&:hover': {
                  bgcolor: '#f8fafc',
                },
              }}
            >
              {heroContent.primaryCTA.text}
            </Button>
            <Button
              variant="outline"
              size="large"
              onClick={handleSecondaryCTA}
              sx={{
                borderColor: 'white',
                color: 'white',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              {heroContent.secondaryCTA.text}
            </Button>
          </Stack>

          {/* Value Points */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 2, sm: 4 }}
            sx={{
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            {heroContent.valuePoints.map((point, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  justifyContent: { xs: 'center', sm: 'flex-start' },
                }}
              >
                <Icon
                  icon={CheckCircleIcon}
                  size="small"
                  sx={{
                    color: 'white',
                  }}
                />
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  }}
                >
                  {point}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      </Container>
    </Box>
  )
}
